import { FC, createContext, useMemo, useCallback, useState } from "react";
import { useAuth } from "../app/modules/auth";
import { TPermissionProvider } from "../types/PermissionProvider";
import { useLocation } from "react-router-dom";
import { requestAccess } from "../request/modulName/moduleRequest";
import { TPermissionContextProps } from "../types/PermissionProvider/PermissionProviderProps";
import { toast } from "react-toastify";
import {
  isAccessible,
  can,
  getCurrentModule,
} from "../utils/PermissionUtils/PermissionUtils";
import { PemissionDenied } from "../components/Error/PemissionDenied";

const initPermissionContextPropsState = {
  isAllowed: false,
  currentModule: {},
  hasAccessTo: () => false,
  hasAccess: () => false,
  requestAccess: () => undefined,
  canDo: () => false,
  currentActivity: undefined,

};
const PermissionContext = createContext<TPermissionContextProps>(
  initPermissionContextPropsState
);

// Helper function to determine activity type from pathname
const getActivityTypeFromPath = (pathname: string): string => {
  const pathLower = pathname.toLowerCase();
  
  if (pathLower.includes('/edit') ) {
    return 'edit';
  } else if (pathLower.includes('/delete') ) {
    return 'delete';
  } else if (pathLower.includes('/add')) {
    return 'add';
  } else if (pathLower.includes('/view') || pathLower.split('/').length === 3) {
    
    return 'view';
  }
  
  return 'view'; 
};


const isActivityAllowed = (
  moduleName: string,
  activityType: string,
  permissions: any[]
): boolean => {
  // Find the module in permissions
  const module = permissions.find(
    (perm) => perm.moduleName?.toLowerCase() === moduleName.toLowerCase()
  );

  if (!module || !module.moduleActivities) {
    return false;
  }


  const activityFunctionMap: Record<string, string> = {
    view: 'view',
    edit: 'edit',
    delete: 'delete',
    add: 'add',
    create: 'add',
  };

  const activityFunction = activityFunctionMap[activityType] || activityType;

  
  const activity = module.moduleActivities.find(
    (act: any) => act.activityFunction === activityFunction
  );

  // Return isSelected status
  return activity ? activity.isSelected : false;
};

const PermissionProvider: FC<TPermissionProvider> = ({
  children,
  fallback = true,
}) => {
  const { currentUser } = useAuth();
  // console.log(currentUser.permissions)
  const location = useLocation();
  const [currentAction, setCurrentAction] = useState<string>("");

  const sendAccessRequest = useCallback((payload: object) => {
    const fn = async () => {
      try {
        const response = await requestAccess(payload);
        if (response.data.status) {
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("Network error");
      }
      setCurrentAction("");
    };
    fn();
  }, []);

  let contextValue = useMemo(() => {
    const currentActivityValue = currentAction;
    const currentModuleValue = getCurrentModule(location.pathname.split("/")[1]);
    const moduleName = location.pathname.split("/")[1]?.toLowerCase().replace(/\//g, "");
    
    const activityType = getActivityTypeFromPath(location.pathname);
    
    // Module level access
    const hasModuleAccess = currentUser && currentUser.permissions
      ? isAccessible(moduleName, currentUser.permissions)
      : false;
    
    
    const hasActivityAccess = currentUser && currentUser.permissions && hasModuleAccess
      ? isActivityAllowed(moduleName, activityType, currentUser.permissions)
      : false;
    
    
    const isOnActionRoute = ['edit', 'delete', 'add'].includes(activityType);
    const isAllowedValue = isOnActionRoute 
      ? (hasModuleAccess && hasActivityAccess)
      : hasModuleAccess;
  
    return {
      currentActivity: activityType,
      currentModule: currentModuleValue,
      isAllowed: isAllowedValue,
      
     
      checkPermission: (module: string, activity: string) => {
        if (!currentUser || !currentUser.permissions) return false;
        return isActivityAllowed(module, activity, currentUser.permissions);
      },
      
      hasAccessTo: (moduleName: string, activity: string) => {
        if (currentUser && currentUser.permissions) {
          return can(moduleName, activity, currentUser.permissions);
        }
        return false;
      },
      canDo: (moduleName: string, activity: string) => {
        if (contextValue.hasAccessTo(moduleName, activity)) {
          return true;
        } else {
          setCurrentAction(activity);
          return false;
        }
      },
      
      hasAccess: (moduleName: string) => {
        if (currentUser && currentUser.permissions) {
          return isAccessible(moduleName, currentUser.permissions);
        }
        return false;
      },
      
      requestAccess: (module: object) => {
        sendAccessRequest(module);
      },
    };
  }, [
    currentUser,
    location.pathname,
    sendAccessRequest,
    currentAction,
  ]);


  if (fallback && !contextValue.isAllowed) {
    const activityType = getActivityTypeFromPath(location.pathname);
    const activityName = activityType.charAt(0).toUpperCase() + activityType.slice(1);
    
    return (
      <PemissionDenied
        title="Access Denied"
        text={`You do not have permission to ${activityName} in this module.`}
        // buttonText="Request Permission"
        image="/media/auth/permission-error-light.svg"
        handleButtonClick={() => {
          if (contextValue.currentModule) {
            contextValue.requestAccess(contextValue.currentModule);
          } else {
            toast.error("Module does not exist");
          }
        }}
      />
    );
  }

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

export { PermissionProvider, PermissionContext };